import { z } from "zod";
import { PropertyDataSchema, VisualFeaturesSchema } from "./geocoding.schema";
import { optionalUuid } from "./_helpers/optional";
import { ExternalIdEntrySchema } from "./external-id.schema";

// ADV-909: locations are account-scoped — business_id column dropped.
export const LocationSchema = z.object({
  id: z.string().uuid().describe("ID"),
  account_id: z.string().uuid().describe("Account"),
  street_line_1: z.string().nullable().describe("Street Line 1"),
  street_line_2: z.string().nullable().describe("Street Line 2"),
  city: z.string().nullable().describe("City"),
  state: z.string().nullable().describe("State"),
  postal_code: z.string().nullable().describe("Postal Code"),
  country: z.string().nullable().describe("Country"),
  formatted_address: z.string().nullable().describe("Formatted Address"),
  latitude: z.number().nullable().describe("Latitude"),
  longitude: z.number().nullable().describe("Longitude"),
  geocode_accuracy: z.string().nullable().describe("Geocode Accuracy"),
  geocode_source: z.string().nullable().describe("Geocode Source"),
  geocoded_at: z.string().nullable().describe("Geocoded At"),
  is_validated: z.boolean().nullable().describe("Validated"),
  validation_source: z.string().nullable().describe("Validation Source"),
  validated_at: z.string().nullable().describe("Validated At"),
  address_hash: z.string().nullable().describe("Address Hash"),
  location_type: z.string().nullable().describe("Location Type"),
  notes: z.string().nullable().describe("Notes"),
  metadata: z.record(z.unknown()).nullable().describe("Metadata"),
  precision_type: z.string().nullish().describe("Precision Type"),
  image_url: z.string().nullable().describe("Image URL"),
  // ADV-733: Smarty parcel enrichment (lot size, year built, roof type, etc.).
  // Populated by demo-data-generator's curate step (Pass 1.5) and consumed by
  // downstream grounding (visual feature extraction, scene generation).
  property_data: PropertyDataSchema.nullish().describe("Property Data"),
  // ADV-738: Gemini-as-vision-extractor output. Populated by Pass 2.5 from
  // NAIP overhead + Mapillary street-level tiles. Null when extraction was
  // skipped or the address had no usable imagery.
  visual_features: VisualFeaturesSchema.nullish().describe("Visual Features"),
  // ADV-738: Which imagery sources Gemini analyzed.
  extraction_source: z.string().nullish().describe("Extraction Source"),
  // ADV-755: When and with which model extraction was last run.
  extracted_at: z.string().nullish().describe("Extracted At"),
  model_version: z.string().nullish().describe("Model Version"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type Location = z.infer<typeof LocationSchema>;

// ADV-211: `is_primary` on `core.entity_locations` is **system-decided** for
// import payloads. The first contact_role linked to a given location wins the
// primary flag; subsequent links from the importer are recorded with
// `is_primary=false`. Re-runs are idempotent (exact-tuple pre-check short-
// circuits duplicate writes), so the primary assignment is stable across
// shuffled batch order and replays. Callers therefore MUST NOT send
// `is_primary` through the import payload — it is not accepted here.
// See `upsertLocationCustomerJunction` in `import-entity-handlers.ts`.
export const LocationInsertSchema = z.object({
  street_line_1: z.string().optional().describe("Street Line 1"),
  street_line_2: z.string().optional().describe("Street Line 2"),
  city: z.string().optional().describe("City"),
  state: z.string().optional().describe("State"),
  postal_code: z.string().optional().describe("Postal Code"),
  country: z.string().optional().describe("Country"),
  formatted_address: z.string().optional().describe("Formatted Address"),
  latitude: z.number().optional().describe("Latitude"),
  longitude: z.number().optional().describe("Longitude"),
  location_type: z.string().optional().describe("Location Type"),
  notes: z.string().optional().describe("Notes"),
  metadata: z.any().optional().describe("Metadata"),
  image_url: z.string().optional().describe("Image URL"),
  // ADV-889 / ADR-0058: `property_data` is accepted again, but the location
  // import handler routes it to `core.property_enrichment_cache` (keyed by
  // address_hash + provider) — there is still no `property_data` column on
  // `core.locations`. ADV-1096 closed the original PGRST204 by stripping
  // the field; ADR-0058 reopens it because the demo-data-generator must be
  // able to seed enrichment without a second pass through the external
  // provider. Wire-up: `import-entity-handlers.ts` `location.create`.
  property_data: PropertyDataSchema.nullish().describe("Property Data"),
  visual_features: VisualFeaturesSchema.nullish().describe("Visual Features"),
  extraction_source: z.string().nullish().describe("Extraction Source"),
});

export const LocationUpdateSchema = LocationInsertSchema.partial();

// ADV-889 / ADR-0058: provider-sourced enrichment columns that the bespoke
// location import handler routes into `core.location_enrichments` (1:1
// satellite keyed on location_id). Mirrors the column set declared in
// `supabase/migrations/*_adv_889_location_enrichments_satellite.sql`.
// Single source of truth for: (a) what the importer accepts beyond the OLTP
// row, (b) what the handler writes to the satellite, and (c) what the
// schema-drift guard at `demo-data-generator/src/__tests__/location-schema-drift.test.ts`
// validates against `GeneratedLocation`.
export const LocationEnrichmentInputSchema = z.object({
  // NAIP / Mapillary
  naip_clarity: z
    .enum(["clear", "obscured"])
    .nullish()
    .describe("NAIP Clarity"),
  naip_overhead_url: z.string().nullish().describe("NAIP Overhead URL"),
  mapillary_coverage: z
    .enum(["has_photo", "none"])
    .nullish()
    .describe("Mapillary Coverage"),
  mapillary_image_urls: z
    .array(z.string())
    .nullish()
    .describe("Mapillary Image URLs"),
  mapillary_attributions: z
    .array(z.record(z.unknown()))
    .nullish()
    .describe("Mapillary Attributions"),
  mapillary_selection_metadata: z
    .array(z.record(z.unknown()))
    .nullish()
    .describe("Mapillary Selection Metadata"),

  // Google tiles
  google_satellite_url: z.string().nullish().describe("Google Satellite URL"),
  google_satellite_annotated_url: z
    .string()
    .nullish()
    .describe("Google Satellite Annotated URL"),
  google_satellite_meters_per_pixel: z
    .number()
    .nullish()
    .describe("Google Satellite Meters Per Pixel"),
  google_satellite_ground_coverage_m: z
    .number()
    .nullish()
    .describe("Google Satellite Ground Coverage (m)"),
  google_streetview_url: z.string().nullish().describe("Google Streetview URL"),
  google_streetview_metadata: z
    .record(z.unknown())
    .nullish()
    .describe("Google Streetview Metadata"),

  // Building footprint (jsonb today; see ADR-0058 for PostGIS promotion).
  building_footprint_geojson: z
    .record(z.unknown())
    .nullish()
    .describe("Building Footprint GeoJSON"),
  building_footprint_area_sqft: z
    .number()
    .nullish()
    .describe("Building Footprint Area (sqft)"),
  building_footprint_perimeter_ft: z
    .number()
    .nullish()
    .describe("Building Footprint Perimeter (ft)"),

  // Overture
  overture_building_class: z
    .string()
    .nullish()
    .describe("Overture Building Class"),
  overture_building_subtype: z
    .string()
    .nullish()
    .describe("Overture Building Subtype"),
  overture_places_commercial_count: z
    .number()
    .int()
    .nullish()
    .describe("Overture Places Commercial Count"),
  overture_places_top_categories: z
    .array(z.string())
    .nullish()
    .describe("Overture Places Top Categories"),
  overture_property_inference: z
    .enum(["commercial", "residential", "unknown"])
    .nullish()
    .describe("Overture Property Inference"),
  overture_business_name: z
    .string()
    .nullish()
    .describe("Overture Business Name"),
  overture_primary_category: z
    .string()
    .nullish()
    .describe("Overture Primary Category"),

  // Places
  place_id: z.string().nullish().describe("Place ID"),
  place_business_name: z.string().nullish().describe("Place Business Name"),
  place_primary_type: z.string().nullish().describe("Place Primary Type"),

  // Coverage scoring
  coverage_score: z.number().nullish().describe("Coverage Score"),
});
export type LocationEnrichmentInput = z.infer<
  typeof LocationEnrichmentInputSchema
>;
export const LOCATION_ENRICHMENT_COLUMNS = Object.keys(
  LocationEnrichmentInputSchema.shape,
) as ReadonlyArray<keyof LocationEnrichmentInput>;

export const LocationQuerySchema = z.object({
  state: z.string().optional(),
  city: z.string().optional(),
  postal_code: z.string().optional(),
  location_type: z.string().optional(),
  // ADV-782: filter to locations assigned (via customer) to a given territory.
  territory_id: optionalUuid(),
});

// === Entity Locations ===

export const EntityLocationSchema = z.object({
  id: z.string().uuid().describe("ID"),
  location_id: z.string().uuid().describe("Location"),
  entity_type: z.string().describe("Entity Type"),
  entity_id: z.string().uuid().describe("Entity"),
  relationship_type: z.string().describe("Relationship Type"),
  is_primary: z.boolean().nullable().describe("Primary"),
  effective_from: z.string().nullable().describe("Effective From"),
  effective_to: z.string().nullable().describe("Effective To"),
  created_at: z.string().nullable().describe("Created At"),
});
export type EntityLocation = z.infer<typeof EntityLocationSchema>;
